import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Card } from '@shared/ui/Card';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { useListKadryNotificationsQuery, useReadKadryNotificationMutation } from '@shared/api';
import { kadryDateTime, type KadryNotification } from '@entities/kadry';
import { KadryErrorBanner, KadryMuted, KadryPageHeader, KadryStack } from './ui';

const Item = styled(Card)<{ $unread: boolean }>`
  border-left: 4px solid
    ${({ theme, $unread }) => ($unread ? theme.colors.primary : 'transparent')};
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  font-weight: 600;
`;

const Body = styled.div`
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TONE: Record<KadryNotification['severity'], 'info' | 'warning' | 'danger'> = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
};

const TONE_TEXT: Record<KadryNotification['severity'], string> = {
  info: 'событие',
  warning: 'важно',
  critical: 'срочно',
};

/** Уведомления модуля: ответы кандидатов, новые кандидаты, тревоги. */
export function KadryNotifications() {
  const { data, isLoading, error, refetch } = useListKadryNotificationsQuery();
  const [read] = useReadKadryNotificationMutation();
  return (
    <>
      <KadryPageHeader
        title="Уведомления"
        subtitle="Ответы кандидатов, новые кандидаты по заявкам и тревоги (например, кандидату не перезвонили). Приходят сразу, без обновления страницы."
      />
      {error ? <KadryErrorBanner error={error} onRefresh={refetch} /> : null}
      {isLoading ? (
        <SkeletonLine $width="60%" />
      ) : !data || data.length === 0 ? (
        <KadryMuted>Уведомлений нет.</KadryMuted>
      ) : (
        <KadryStack>
          {data.map((n) => {
            const unread = n.read_at == null;
            return (
              <Item key={n.id} $unread={unread}>
                <Head>
                  <Badge $tone={TONE[n.severity]}>{TONE_TEXT[n.severity]}</Badge>
                  {n.title}
                </Head>
                <Body>{n.body}</Body>
                <Foot>
                  <span>{kadryDateTime(n.created_at)}</span>
                  {n.ref_type === 'request' && n.ref_id && (
                    <Link to={`/agents/kadry/requests/${n.ref_id}`} onClick={() => unread && read(n.id)}>
                      Открыть заявку
                    </Link>
                  )}
                  {n.ref_type === 'source' || n.ref_type === 'channel' ? (
                    <Link to="/agents/kadry/settings/connections" onClick={() => unread && read(n.id)}>
                      Открыть подключения
                    </Link>
                  ) : null}
                  {unread && (
                    <Button type="button" $size="sm" $variant="ghost" onClick={() => read(n.id)}>
                      Прочитано
                    </Button>
                  )}
                </Foot>
              </Item>
            );
          })}
        </KadryStack>
      )}
    </>
  );
}
